import { z } from "zod"
import { apiResponseSchema, baseResponseSchema, paginatedResponseSchema } from "./shared"
import { appointmentSchema } from "./appointment"

export const businessBranchSummarySchema = z.object({
  _id: z.string().optional(),
  name: z.string().optional(),
  image: z.string().optional(),
  location: z.string().optional(),
  industry: z
    .array(
      z.union([
        z.string(),
        z
          .object({
            name: z.string().optional(),
          })
          .passthrough(),
      ])
    )
    .optional(),
})

export const businessSchema = z
  .object({
    _id: z.string(),
    name: z.string().optional(),
    description: z.string().optional(),
    image: z.string().optional(),
    industry: z
      .array(
        z.union([
          z.string(),
          z
            .object({
              name: z.string().optional(),
            })
            .passthrough(),
        ])
      )
      .optional(),
    receivingAccount: z
      .object({
        account_number: z.string().optional(),
        bank_name: z.string().optional(),
      })
      .optional(),
    parentBusiness: z
      .union([z.string(), businessBranchSummarySchema.extend({ _id: z.string().optional() })])
      .nullish(),
    branches: z.array(businessBranchSummarySchema).optional(),
    branchCount: z.number().optional(),
    isBranch: z.boolean().optional(),
    location: z.string().optional(),
  })
  .passthrough()

export type BusinessSchema = z.infer<typeof businessSchema>

export const industrySchema = z
  .object({
    _id: z.string().optional(),
    name: z.string().optional(),
  })
  .passthrough()

export const incomeMetricsSchema = z
  .object({
    cardBalance: z.number().optional(),
    withdrawableAmountStats: z
      .object({
        totalAmount: z.number().optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough()

export const businessResponseSchema = apiResponseSchema(businessSchema)

export const businessesListResponseSchema = baseResponseSchema.extend({
  data: z.array(businessSchema),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  totalPages: z.number().optional(),
  totalItems: z.number().optional(),
})

// Form validation schema for business creation
export const createBusinessFormSchema = z.object({
  // Mirrors CreateBusinessDto on the API (@MinLength(5) / @MaxLength(150)) so a short
  // name surfaces inline instead of coming back as a 400 after submit.
  businessName: z.preprocess(
    (val) => (val === undefined || val === null ? "" : val),
    z.string().superRefine((value, ctx) => {
      const trimmed = value.trim()
      if (!trimmed) {
        ctx.addIssue({
          code: "custom",
          message: "Business or institution name is required",
        })
        return
      }
      if (trimmed.length < 5) {
        ctx.addIssue({
          code: "custom",
          message: "Name must be at least 5 characters",
        })
      }
      if (trimmed.length > 150) {
        ctx.addIssue({
          code: "custom",
          message: "Name must be at most 150 characters",
        })
      }
    })
  ),
  businessDesc: z.string().optional(),
  industry: z.string().optional(),
  location: z.string().optional(),
  email: z.union([
    z.literal(""),
    z.string().email("Email is invalid"),
  ]).optional(),
  phone: z.preprocess(
    (val) => (val === undefined || val === null ? "" : val),
    z.string().min(1, "Phone number is required")
  ),
  ownerName: z.string().optional(),
  ownerEmail: z.union([z.literal(""), z.string().email("Invalid email address")]).optional(),
  ownerPhone: z.string().optional(),
})

/** Edit business: location is bound outside Formik (map section); validate text in submit handler */
export const editBusinessFormSchema = createBusinessFormSchema.omit({ location: true })

export const industriesResponseSchema = baseResponseSchema.extend({
  data: z
    .array(industrySchema)
    .nullable()
    .transform((d) => d ?? []),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  totalPages: z.number().optional(),
  totalItems: z.number().optional(),
})

export const industrySingleResponseSchema = apiResponseSchema(industrySchema)
export const appointmentsResponseSchema = paginatedResponseSchema(appointmentSchema)
/** Backend returns metrics at the root (not always wrapped in `data`). */
export const incomeMetricsResponseSchema = z.preprocess((raw) => {
  if (!raw || typeof raw !== "object") return raw
  const obj = raw as Record<string, unknown>
  if (obj.data && typeof obj.data === "object") return obj
  if (
    "cardBalance" in obj ||
    "withdrawableAmountStats" in obj ||
    "transactionStatistics" in obj
  ) {
    return {
      message: typeof obj.message === "string" ? obj.message : undefined,
      error: obj.error ?? "00",
      data: obj,
    }
  }
  return obj
}, apiResponseSchema(incomeMetricsSchema))

export const dashboardAppointmentSchema = z.object({
  _id: z.string().optional(),
  appointmentId: z.string().optional(),
  customer: z.object({
    _id: z.string().optional(),
    name: z.string().optional(),
    image: z.string().optional().nullable(),
  }).optional().nullable(),
  service: z.object({
    _id: z.string().optional(),
    name: z.string().optional(),
  }).optional().nullable(),
  appointmentStartTime: z.string().optional().nullable(),
  appointmentEndTime: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
}).passthrough()

export const businessDashboardSchema = z.object({
  kpis: z.object({
    totalBookings: z.number(),
    totalRevenue: z.number(),
    totalCustomers: z.number(),
    averageRating: z.number(),
  }),
  revenueOverTime: z.array(
    z.object({
      month: z.string(),
      revenue: z.number(),
    })
  ),
  customerGrowth: z.array(
    z.object({
      month: z.string(),
      newCustomers: z.number(),
      returningCustomers: z.number(),
    })
  ),
  appointmentStatus: z.array(
    z.object({
      status: z.string(),
      count: z.number(),
    })
  ),
  servicePerformance: z.array(
    z.object({
      serviceName: z.string(),
      bookings: z.number(),
      revenue: z.number(),
    })
  ),
  peakHours: z.array(
    z.object({
      hour: z.string(),
      bookings: z.number(),
    })
  ),
  ratingsDistribution: z.array(
    z.object({
      rating: z.number(),
      count: z.number(),
    })
  ),
  completedBookingsForTenDays: z.object({
    dates: z.array(z.string()),
    values: z.array(z.number()),
  }).optional(),
  serviceFulfillmentRate: z.number().optional(),
  todaysAppointments: z.array(dashboardAppointmentSchema).optional(),
})

export type BusinessDashboardSchema = z.infer<typeof businessDashboardSchema>
export const businessDashboardResponseSchema = baseResponseSchema.extend({
  ...businessDashboardSchema.shape,
})
